import { useEffect, useState, useMemo, useCallback, memo } from "react";
import { useTranslation } from "react-i18next";
import { Bar } from "react-chartjs-2";
import { http } from "../helper";
import { BaseUrl } from "../common/config";
import Chart from "chart.js/auto";
import { CategoryScale } from "chart.js";
Chart.register(CategoryScale);

export const BarChart = memo(({}) => {
  const { t } = useTranslation();
  const { currentYear, currentMonth } = useMemo(() => {
    const now = new Date();
    return {
      currentYear: now.getFullYear(),
      currentMonth: now.getUTCMonth() + 1,
    };
  }, []);
  const [reportType, setReportType] = useState("weekly");
  // helper to compute current FY
  const getCurrentFiscalYear = useCallback(() => {
    const today = new Date();
    const year = today.getFullYear();
    const month = today.getMonth() + 1;
    // Fiscal year starts in September
    const startYear = month >= 9 ? year : year - 1;
    return `${startYear}-${startYear + 1}`;
  }, []);
  const [period, setPeriod] = useState(getCurrentFiscalYear());
  const [status, setStatus] = useState("all");
  const [isLoading, setIsLoading] = useState(true);
  // Custom plugin to display "No data found"
  const noDataPlugin = useMemo(
    () => ({
      id: "noDataPlugin",
      beforeDraw: (chart) => {
        const { datasets } = chart.data;
        const hasData = datasets.some((dataset) => dataset.data.length > 0);
        if (!hasData) {
          const ctx = chart.ctx;
          const { width, height } = chart;
          ctx.save();
          ctx.clearRect(0, 0, width, height);
          ctx.textAlign = "center";
          ctx.textBaseline = "middle";
          ctx.font = "20px Arial";
          ctx.fillStyle = "#eb9f9d";
          ctx.fillText(t("no_data_filtered_search_txt"), width / 2, height / 2);
          ctx.restore();
        }
      },
    }),
    [t],
  );

  const [topSellerChartData, setTopSellerChartData] = useState({
    labels: [],
    datasets: [
      {
        label: t("top_selling_products_txt"),
        data: [],
        backgroundColor: ["rgba(252,194,84,1)", "#f4ecec"],
        borderColor: "#e6e6e6",
        borderWidth: 2,
      },
    ],
  });

  const memoQuarterNames = useMemo(
    () => ({
      1: t("quarter_sep_nov"),
      2: t("quarter_dec_feb"),
      3: t("quarter_mar_may"),
      4: t("quarter_jun_aug"),
    }),
    [t],
  );

  const memoDayNames = useMemo(
    () => [
      t("days.sun"),
      t("days.mon"),
      t("days.tue"),
      t("days.wed"),
      t("days.thu"),
      t("days.fri"),
      t("days.sat"),
    ],
    [t],
  );

  const buildChartData = useCallback(
    (chartData) => {
      const labels = chartData.map((item) => {
        if (reportType === "quarterly") {
          return memoQuarterNames[item._id] || item._id;
        }
        if (item._id?.month !== undefined) {
          return `${item._id.month}/${item._id.year}`;
        }
        if (item._id?.year !== undefined) {
          return item._id.year;
        }
        if (item._id?.dateString !== undefined) {
          return `${item._id.dateString} ${memoDayNames[item._id.dayOfWeek - 1]}`;
        }
        return item._id;
      });
      return {
        labels,
        datasets: [
          {
            label: t("total_revenue_txt"),
            data: chartData.map((item) => item.amount.toFixed(2)),
            backgroundColor: ["#8dc5e4", "#1b81bc"],
            borderColor: "#E5E5E5",
            borderWidth: 0,
            barThickness: 40,
            maxBarThickness: 60,
            borderRadius: 4,
          },
        ],
      };
    },
    [memoDayNames, memoQuarterNames, t, reportType],
  );

  useEffect(() => {
    setIsLoading(true);
    const url =
      reportType === "quarterly"
        ? BaseUrl + "admin/quarterlyreport"
        : BaseUrl + "admin/getrevenuereport";
    const payload =
      reportType === "quarterly"
        ? { period, status }
        : { reportType, period, status };
    http
      .post(url, payload)
      .then((response) => {
        setIsLoading(false);
        const chartData = response.data.data.filter((item) => item.amount > 0);
        setTopSellerChartData(buildChartData(chartData));
      })
      .catch((err) => {
        setIsLoading(false);
        console.log(err);
      });
  }, [buildChartData, period, reportType, status]);

  const changeHandler = useCallback((e) => {
    setReportType(e.target.value);
  }, []);

  const changeStatusHandler = useCallback((e) => {
    setStatus(e.target.value);
  }, []);

  const changePeriodHandler = useCallback((e) => {
    setPeriod(e.target.value);
  }, []);

  const options = useMemo(() => {
    const list = [];
    for (let i = currentYear; i >= 2023; i--) {
      if (i === currentYear && currentMonth < 9) {
        continue;
      }
      list.push(
        <option key={i} value={`${i}-${i + 1}`}>
          {`${i}-${i + 1}`}
        </option>,
      );
    }
    return list;
  }, [currentYear, currentMonth]);
  let loadingbuffer = isLoading ? "loading-buffer" : "";

  return (
    <div className={`chart-container chart-revenue ${loadingbuffer}`}>
      <div className="chart-Hdr">
        <h2 style={{ textAlign: "left" }}>{t("revenue_analysis_txt")}</h2>
        <div className="chart-fltr">
          <select
            style={{ width: "170px" }}
            value={status}
            onChange={(e) => changeStatusHandler(e)}
          >
            {" "}
            <option value={"all"}>{t("all_txt")}</option>
            <option value={"New Order"}>{t("new_order_txt")}</option>
            <option value={"Acknowledged"}>{t("acknowledged_txt")}</option>
            <option value={"Invoiced"}>{t("invoiced_txt")}</option>
            <option value={"Part Desp.  Inv."}>{t("part_desp_inv_txt")}</option>
            <option value={"Acknl  Being Picked"}>
              {t("acknl_being_picked_txt")}
            </option>
          </select>
          <select
            style={{ width: "150px" }}
            value={reportType}
            onChange={(e) => changeHandler(e)}
          >
            {" "}
            <option value={"weekly"}>{t("current_week_txt")}</option>
            <option value={"7days"}>{t("past_7_days_txt")}</option>
            <option value={"monthly"}>{t("monthly_txt")}</option>
            <option value={"quarterly"}>{t("quarterly_txt")}</option>
            <option value={"yearly"}>{t("fy_data_txt")}</option>
          </select>
          {(reportType === "monthly" ||
            reportType === "quarterly" ||
            reportType === "yearly") && (
            <select
              style={{ width: "170px" }}
              value={period}
              onChange={changePeriodHandler}
            >
              {options}
            </select>
          )}
        </div>
      </div>
      {topSellerChartData && (
        <Bar
          data={topSellerChartData}
          plugins={[noDataPlugin]}
          options={{
            scales: {
              y: {
                grid: {
                  color: "rgba(247, 243, 241, 0.8)",
                },
                ticks: {
                  color: "rgba(207, 208, 211, 1)",
                },
              },
              x: {
                grid: {
                  color: "rgba(247, 243, 241, 0.8)",
                },
                ticks: {
                  color: "rgba(207, 208, 211, 1)",
                  callback: function (value, index, ticks) {
                    const label = this.getLabelForValue(value); // Get the label for the current tick
                    if ((reportType === "7days" || reportType === "weekly") && typeof label === "string" && label.includes(" ")) {
                      const [date, dayOfWeek] = label.split(" "); // Split by the comma separator
                      return [dayOfWeek, date]; // Return the label with a newline
                    }
                    return label; // Single-line label for other cases
                  },
                },
              },
            },
            layout: {
              padding: {
                top: 20,
                right: 0,
                bottom: 0,
                left: 0,
              },
            },
            plugins: {
              title: {
                display: false,
                text: t("revenue_analysis_txt"),
              },
              legend: {
                display: false,
              },
              tooltip: {
                titleColor: "rgba(220, 222, 226, 1)",
                bodyColor: "rgba(220, 222, 226, 1)",
              },
              datalabels: {
                display: false,
                font: {
                  size: 11,
                  weight: "600",
                },
                padding: 5,
                anchor: "end",
                align: "top",
                formatter: function (value) {
                  return "£" + value;
                },
              },
            },
          }}
        />
      )}
    </div>
  );
});
